import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, X, Eye, Hand, GitBranch, MessageCircle } from 'lucide-react';
import { AYUDA, TOTAL_PASOS } from '../config/ayudaContextual';

interface AyudaContextualProps {
  /** Clave de la pantalla en config/ayudaContextual.ts (ej: 'cocina'). */
  seccion: string;
}

/** Bloque titulado con lista de viñetas. */
const Bloque: React.FC<{
  icono: React.ReactNode;
  titulo: string;
  items: string[];
}> = ({ icono, titulo, items }) => (
  <section>
    <div className="flex items-center gap-2 mb-2">
      <span className="text-blue-600 dark:text-blue-400">{icono}</span>
      <h3 className="font-semibold text-gray-900 dark:text-white">{titulo}</h3>
    </div>
    <ul className="space-y-1.5 pl-1">
      {items.map((texto, i) => (
        <li
          key={i}
          className="flex gap-2 text-sm text-gray-700 dark:text-gray-300"
        >
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-400" />
          <span>{texto}</span>
        </li>
      ))}
    </ul>
  </section>
);

/**
 * Botón "?" de ayuda contextual + panel lateral (drawer) a la derecha.
 * Se coloca al lado del título de cada pantalla. Solo frontend (sin backend).
 */
const AyudaContextual: React.FC<AyudaContextualProps> = ({ seccion }) => {
  const [abierto, setAbierto] = useState(false);
  const ayuda = AYUDA[seccion];

  // Cerrar con tecla Escape y bloquear scroll del fondo mientras está abierto.
  useEffect(() => {
    if (!abierto) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [abierto]);

  if (!ayuda) return null;

  const pasoTexto = ayuda.auxiliar
    ? 'Pantalla auxiliar · fuera del flujo numerado'
    : `Paso ${ayuda.paso} de ${TOTAL_PASOS}`;

  return (
    <>
      <button
        type="button"
        onClick={() => setAbierto(true)}
        title="¿Qué es esta pantalla?"
        aria-label="Abrir ayuda de esta pantalla"
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 transition-colors hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-900/70"
      >
        <HelpCircle className="h-5 w-5" />
      </button>

      {abierto &&
        createPortal(
          <div className="fixed inset-0 z-[1000]">
            {/* Fondo: cierra al hacer click fuera */}
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setAbierto(false)}
            />

            {/* Panel lateral derecho */}
            <aside
              role="dialog"
              aria-modal="true"
              aria-label={`Ayuda: ${ayuda.titulo}`}
              className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col bg-white shadow-2xl dark:bg-gray-800"
            >
              {/* Encabezado */}
              <div className="flex items-start justify-between gap-3 border-b border-gray-200 p-5 dark:border-gray-700">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">
                    Ayuda de la pantalla
                  </p>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                    {ayuda.titulo}
                  </h2>
                  <span className="mt-1 inline-block rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                    {pasoTexto}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setAbierto(false)}
                  aria-label="Cerrar ayuda"
                  className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Cuerpo */}
              <div className="flex-1 space-y-6 overflow-y-auto p-5">
                <Bloque
                  icono={<Eye className="h-4 w-4" />}
                  titulo="Qué muestra esta pantalla"
                  items={ayuda.queMuestra}
                />
                <Bloque
                  icono={<Hand className="h-4 w-4" />}
                  titulo="Qué puedes hacer aquí"
                  items={ayuda.quePuedesHacer}
                />

                {/* Cómo encaja en el flujo (lo más importante) */}
                <section>
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-blue-600 dark:text-blue-400">
                      <GitBranch className="h-4 w-4" />
                    </span>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      Cómo encaja en el flujo
                    </h3>
                  </div>
                  <div className="space-y-2 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-900/50 dark:bg-blue-900/20">
                    <p className="text-sm font-bold text-blue-800 dark:text-blue-300">
                      {pasoTexto}
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {ayuda.comoEncaja.resumen}
                    </p>
                    {ayuda.comoEncaja.antes && (
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        ◀ Antes — {ayuda.comoEncaja.antes}
                      </p>
                    )}
                    {ayuda.comoEncaja.despues && (
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        ▶ Después — {ayuda.comoEncaja.despues}
                      </p>
                    )}
                  </div>
                </section>
              </div>

              {/* Caja de cierre / feedback */}
              <div className="border-t border-gray-200 p-5 dark:border-gray-700">
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/50 dark:bg-amber-900/20">
                  <MessageCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    ¿Algo no se entiende o no calza con lo que ves? Anótalo y
                    cuéntale a tu contacto de SuMinuta — esta ayuda se irá
                    afinando.
                  </p>
                </div>
              </div>
            </aside>
          </div>,
          document.body
        )}
    </>
  );
};

export default AyudaContextual;
